import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Sparkles, Play, History } from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";

export default function ModuleRun() {
    const { slug } = useParams();
    const [detail, setDetail] = useState(null);
    const [inputs, setInputs] = useState({});
    const [output, setOutput] = useState(null);
    const [error, setError] = useState(null);
    const [running, setRunning] = useState(false);
    const [runs, setRuns] = useState([]);

    const load = useCallback(async () => {
        try {
            const d = await api.moduleDetail(slug);
            setDetail(d);
            // Prefill inputs with defaults where possible
            const seed = {};
            for (const f of d.module.input_schema || []) {
                seed[f.name] = f.type === "multi_select" || f.type === "tags" ? [] : "";
            }
            setInputs(seed);
        } catch (e) {
            toast.error(e.message || "Failed to load");
        }
        try {
            const r = await api.moduleMyRuns();
            const filtered = (r.items || []).filter((x) => x.module_slug === slug);
            setRuns(filtered.slice(0, 10));
        } catch {
            /* ignore */
        }
    }, [slug]);

    useEffect(() => {
        load();
    }, [load]);

    const canRun = useMemo(() => {
        if (!detail) return false;
        for (const f of detail.module.input_schema || []) {
            if (f.required) {
                const v = inputs[f.name];
                if (
                    v === undefined ||
                    v === null ||
                    (typeof v === "string" && v.trim() === "") ||
                    (Array.isArray(v) && v.length === 0)
                ) {
                    return false;
                }
            }
        }
        return true;
    }, [detail, inputs]);

    async function run() {
        if (!detail || !canRun) return;
        setRunning(true);
        setOutput(null);
        setError(null);
        try {
            // Idempotency-Key derived from inputs to allow safe retries
            const key =
                typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `run-${Date.now()}`;
            const res = await api.moduleRun(slug, inputs, key);
            setOutput(res);
            toast.success(`Ran! ${res.credits_spent} credit${res.credits_spent === 1 ? "" : "s"} spent · balance ${res.balance}`);
            // Refresh runs history
            const r = await api.moduleMyRuns();
            setRuns((r.items || []).filter((x) => x.module_slug === slug).slice(0, 10));
        } catch (e) {
            setError(e.data?.detail || { error: e.message || "Run failed" });
            toast.error(e.message || "Run failed");
        } finally {
            setRunning(false);
        }
    }

    if (!detail) {
        return (
            <SidebarLayout>
                <div className="text-text-secondary text-sm">Loading module…</div>
            </SidebarLayout>
        );
    }

    const m = detail.module;
    return (
        <SidebarLayout>
            <div className="mx-auto max-w-6xl">
                <Link
                    to="/modules"
                    className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-main mb-4"
                    data-testid="module-back"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to modules
                </Link>
                <motion.div
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                >
                    <div className="flex items-start justify-between gap-6 flex-wrap">
                        <div className="min-w-0">
                            <h1 className="text-2xl font-semibold" data-testid="module-title">{m.name}</h1>
                            <p className="text-text-secondary text-sm mt-1">{m.description}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] uppercase tracking-widest text-text-secondary">Cost</p>
                            <p className="text-2xl font-semibold">
                                {detail.pricing.effective}
                                <span className="text-sm text-text-secondary ml-1">credits</span>
                            </p>
                            {detail.pricing.multiplier !== 1 && (
                                <p className="text-[10px] text-text-secondary mt-1">
                                    {detail.pricing.base} base × {detail.pricing.multiplier}
                                </p>
                            )}
                        </div>
                    </div>

                    {!detail.unlocked && (
                        <div className="mt-4 rounded-lg border border-[var(--color-alert)]/30 bg-[var(--color-alert)]/10 p-3 text-sm text-[var(--color-alert)]">
                            This module requires plan <b>{m.required_min_plan}</b>. <Link className="underline" to="/app/subscription">Upgrade to unlock</Link>.
                        </div>
                    )}
                </motion.div>

                <div className="mt-6 grid gap-6 lg:grid-cols-5">
                    <div className="lg:col-span-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                        <p className="text-xs uppercase tracking-widest text-text-secondary mb-4">Inputs</p>
                        <div className="space-y-4" data-testid="module-form">
                            {(m.input_schema || []).map((f) => (
                                <FieldRenderer
                                    key={f.name}
                                    field={f}
                                    value={inputs[f.name]}
                                    onChange={(v) => setInputs((s) => ({ ...s, [f.name]: v }))}
                                />
                            ))}
                        </div>
                        <button
                            data-testid="module-run-btn"
                            disabled={!canRun || running || !detail.unlocked}
                            onClick={run}
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#EDEDED] px-5 py-2.5 text-sm font-medium text-black disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Play className="h-4 w-4" />
                            {running ? "Running…" : `Run (${detail.pricing.effective} credits)`}
                        </button>
                    </div>

                    <div className="lg:col-span-2 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 min-h-[280px]">
                        <p className="text-xs uppercase tracking-widest text-text-secondary mb-3">Output</p>
                        {output ? (
                            <OutputRenderer output={output} render={m.output_render} format={m.ai_config.output_format} />
                        ) : error ? (
                            <div
                                data-testid="module-output-error"
                                className="rounded-lg border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 p-3 text-sm text-[var(--color-alert)]"
                            >
                                <p className="font-medium">Run failed</p>
                                <pre className="mt-2 text-xs whitespace-pre-wrap opacity-90">
                                    {typeof error === "string" ? error : JSON.stringify(error, null, 2)}
                                </pre>
                            </div>
                        ) : (
                            <p className="text-text-secondary text-sm">
                                Fill the inputs and hit <span className="text-text-main">Run</span> to see AI output here.
                            </p>
                        )}
                    </div>
                </div>

                {runs.length > 0 && (
                    <div className="mt-8 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                        <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-text-secondary mb-3">
                            <History className="h-3.5 w-3.5" /> Last runs
                        </p>
                        <div className="space-y-2 text-sm" data-testid="module-history">
                            {runs.map((r) => (
                                <div
                                    key={r.id}
                                    className="flex items-center justify-between border-b border-[var(--color-border)] pb-2"
                                >
                                    <span className="text-text-secondary">
                                        {r.credits_spent} credits · {r.execution_time_ms}ms
                                    </span>
                                    <span className="text-text-secondary">
                                        {new Date(r.created_at).toLocaleString()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </SidebarLayout>
    );
}

function FieldRenderer({ field, value, onChange }) {
    const common = "w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2 text-sm outline-none focus:border-[var(--color-primary)]";
    return (
        <div>
            <label className="text-xs text-text-secondary mb-1 block">
                {field.label}
                {field.required && <span className="text-[var(--color-alert)] ml-1">*</span>}
            </label>
            {field.type === "textarea" ? (
                <textarea
                    rows={4}
                    className={common}
                    placeholder={field.placeholder || ""}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`module-input-${field.name}`}
                />
            ) : field.type === "select" ? (
                <select
                    className={common}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`module-input-${field.name}`}
                >
                    <option value="">Choose…</option>
                    {(field.options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            ) : field.type === "number" ? (
                <input
                    type="number"
                    className={common}
                    placeholder={field.placeholder || ""}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`module-input-${field.name}`}
                />
            ) : field.type === "tags" ? (
                <input
                    className={common}
                    placeholder={field.placeholder || "comma,separated,tags"}
                    value={Array.isArray(value) ? value.join(", ") : (value || "")}
                    onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                    data-testid={`module-input-${field.name}`}
                />
            ) : (
                <input
                    className={common}
                    placeholder={field.placeholder || ""}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`module-input-${field.name}`}
                />
            )}
        </div>
    );
}

function OutputRenderer({ output, render, format }) {
    const raw = output.output;
    if (format === "json" || render?.type === "json_table") {
        if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            return (
                <div data-testid="module-output-json" className="space-y-2">
                    {Object.entries(raw).map(([k, v]) => (
                        <div key={k} className="flex flex-col">
                            <p className="text-[10px] uppercase tracking-widest text-text-secondary">{k}</p>
                            <div className="text-sm text-text-main mt-1">
                                {Array.isArray(v) ? (
                                    <ul className="list-disc pl-4 space-y-1">
                                        {v.map((x, i) => <li key={i}>{typeof x === "string" ? x : JSON.stringify(x)}</li>)}
                                    </ul>
                                ) : typeof v === "object" ? (
                                    <pre className="text-xs text-text-secondary bg-[var(--color-panel-ai)] rounded p-2 overflow-x-auto">{JSON.stringify(v, null, 2)}</pre>
                                ) : (
                                    <p>{String(v)}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }
        return <pre data-testid="module-output-json" className="text-xs whitespace-pre-wrap">{JSON.stringify(raw, null, 2)}</pre>;
    }
    if (format === "list" || render?.type === "list_cards") {
        const arr = Array.isArray(raw) ? raw : [];
        return (
            <ul data-testid="module-output-list" className="space-y-2 text-sm">
                {arr.map((x, i) => <li key={i} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2">{x}</li>)}
            </ul>
        );
    }
    if (format === "markdown" || render?.type === "markdown") {
        return (
            <div data-testid="module-output-markdown" className="prose prose-invert prose-sm max-w-none">
                <ReactMarkdown>{String(raw || "")}</ReactMarkdown>
            </div>
        );
    }
    return (
        <p data-testid="module-output-text" className="text-sm text-text-main whitespace-pre-wrap">
            {String(raw || "")}
        </p>
    );
}
